import React, { useCallback, useContext, useMemo } from "react";
import { Empty, List, message, Tooltip } from "antd";
import "./index.less";
import GlobalContext, { GlobalContextConfig } from "@/context/GlobalContext";
import { openLink, time } from "@/components/Joyspace/util";
import { changeEgovUrl } from "@/utils/tools";
import IconFont from "@/components/icon";
import noContent from "@/assets/img/search-nodata.png";
import events from "@/components/Joyspace/context/events";
import Api from "@/components/Joyspace/api";

interface IPpros {
  loading: boolean;
  list: Array<Object>;
  loadMore: Function;
  total: number;
  t: any;
  onClose?: () => void;
}

const GroupSearchList = (props: IPpros) => {
  const { loading, list, loadMore, total, t, onClose, ...rest } = props;
  const globalContext = useContext<GlobalContextConfig>(GlobalContext);

  // 显示加载按钮
  const loadMoreDOM = useMemo(() => {
    return (
      <div>
        {!loading && list.length > 0 ? (
          <div
            className="load-more"
            onClick={(event: any) => {
              event.nativeEvent.stopImmediatePropagation();
            }}
          >
            {total > list.length ? (
              <div
                onClick={() => {
                  loadMore();
                }}
                style={{ cursor: "pointer" }}
              >
                加载更多
              </div>
            ) : (
              <div style={{ color: "rgb(98,101,109)" }}>已全部加载完毕</div>
            )}
          </div>
        ) : null}
      </div>
    );
  }, [total, list, loading, loadMore]);

  // 高亮
  const renderHighlightDOM = useCallback((props: any) => {
    const { highlightDom = "", data = {} } = props;

    if (highlightDom === "title") {
      // title
      const highlightTitle = data.highlight.find((item: any) => item.name === "title");
      if (!highlightTitle) {
        return data.title;
      }
      const { value, key } = highlightTitle;
      const str = value;
      const keyWords = key.split(",");
      const reg = new RegExp(keyWords.join("|"), "gi");
      return str.replace(reg, `<span class="search-highlight">$&</span>`);
    }

    if (highlightDom === "content") {
      // content
      const highlightContent = data.highlight.find((item: any) => item.name === "content");
      if (!highlightContent) {
        if (data.page_type === 1) {
          // 文档类型
          return data.preview_text;
        }
        return;
      }
      const { value, key } = highlightContent;
      const str = value;
      const keyWords = key.split(",");
      const reg = new RegExp(keyWords.join("|"), "gi");
      return str.replace(reg, `<span class="search-highlight">$&</span>`);
    }
  }, []);

  const getFilePath = useCallback((fileNames: string) => {
    const pathNameList = fileNames.split("$@$");
    return pathNameList.length !== 0 ? pathNameList.join("/") : "";
  }, []);

  // 获得当前文档所在文件夹的信息
  const getFolderInfo = useCallback(async (folderId: string) => {
    return await Api.categories.infoWithAuth(folderId, {
      isGroup: true
    });
  }, []);

  const openFolder = useCallback(async (folderId: string) => {
    getFolderInfo(folderId)
      .then((res) => {
        if (res) {
          events.emit("onShowFolderDesc", res.description);
          events.emit("list.check");
          events.emit("groupspace.setPageListFolder", res);
          events.emit("groupspace.setSearchFolderId", res.id);
          onClose?.();
        }
      })
      .catch((err) => {
        message.error(err);
      });
  }, []);

  const renderListItem = useCallback(
    (item: any) => {
      const hasHighlightContent = item.page_type === 1;
      return (
        <List.Item
          key={item.id}
          className={`data-list-item ${hasHighlightContent ? "" : "data-list-item-noContent"}`}
          onClick={(event: any) => {
            event.nativeEvent.stopImmediatePropagation();
            if (item.page_type === 9) {
              // 文件夹
              openFolder(item.id);
            } else {
              openLink(item, globalContext?.appRuntimeEnv.joyspaceHost);
            }
          }}
        >
          <div className="list-item-left">
            <img className="file-icon" src={changeEgovUrl(item.icon_url)} />
          </div>
          <div className="list-item-right">
            <div
              className="file-title"
              dangerouslySetInnerHTML={{
                __html: renderHighlightDOM({ data: item, highlightDom: "title" })
              }}
            />
            {hasHighlightContent && (
              <div
                className="file-text"
                dangerouslySetInnerHTML={{
                  __html: renderHighlightDOM({ data: item, highlightDom: "content" })
                }}
              />
            )}
            <div className="file-other-info">
              {item.author ? (
                <Tooltip title={item.author.name} placement="topLeft">
                  <span className="file-owner">
                    所有者：
                    <span>{item.author.name}</span>
                  </span>
                </Tooltip>
              ) : null}
              <Tooltip
                title={getFilePath(item.full_name)}
                placement="topLeft"
                overlayClassName="file-path-tooltip"
              >
                <a
                  className="file-path"
                  onClick={(e) => {
                    e.stopPropagation();
                    openFolder(item.parent_id);
                  }}
                >
                  <IconFont type="icona-ic_app_im_storage3x" className="file-path-icon" />
                  <span>{item.parent_name}</span>
                </a>
              </Tooltip>
              <div className="file-update-time">{`${time.format(item.modified_at)} 更新`}</div>
            </div>
          </div>
        </List.Item>
      );
    },
    [globalContext, renderHighlightDOM, getFilePath, getFolderInfo]
  );

  if (!loading && !list.length) {
    return (
      <Empty
        {...rest}
        image={noContent}
        description={t("search.nodata")}
        style={{ textAlign: "center", color: "#9B9B9B" }}
        imageStyle={{ textAlign: "center", marginTop: "14px" }}
      />
    );
  } else {
    return (
      <List
        {...rest}
        className="data-list"
        loading={loading}
        dataSource={list}
        renderItem={(item: any) => renderListItem(item)}
        loadMore={loadMoreDOM}
      />
    );
  }
};

export default GroupSearchList;
